import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, LogOut } from 'lucide-react';
import { PromotionCeremony } from '@meridian/release-ceremony';
import { api } from '../../api.js';
import { missionOps, MissionError, type OperatorMe, type RolloutView } from './ceremonyClient.js';
import { Screen, Section, Field, Btn, Badge, EmptyState, Spinner, Rows, ago } from '../../ui/kit.js';

// The ceremony inside Ops. The door is the same locked door: before the
// shared component renders, the operator signs in as the Console owner —
// email, password, authenticator code — through the daemon's proxy to the
// Console's own login. The Ops passkey got you to this screen; it counts
// for nothing beyond it. The candidate is resolved from the GitHub run's
// SHA to the release registry's own id, so both surfaces always mean the
// same release.

type Gate =
  | { step: 'checking' }
  | { step: 'signin'; error?: string }
  | { step: 'mfa'; challengeToken: string; error?: string }
  | { step: 'resolve'; operator: OperatorMe }
  | { step: 'ready'; operator: OperatorMe; candidateId: string }
  | { step: 'norelease'; operator: OperatorMe; detail: string };

export function CeremonyHost() {
  const { runId = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [gate, setGate] = useState<Gate>({ step: 'checking' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [toasts, setToasts] = useState<{ id: number; text: string; tone?: 'error'; detail?: string }[]>([]);
  const rolloutId = params.get('rollout');

  const notify = (text: string, opts?: { tone?: 'error'; detail?: string }) => {
    const id = Date.now();
    setToasts((t) => [...t, { id, text, ...opts }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 6000);
  };

  const resolveCandidate = async (operator: OperatorMe) => {
    setGate({ step: 'resolve', operator });
    try {
      // The Ops candidate is a GitHub run; the registry candidate is the
      // law's own row. The SHA joins them — carried in the link when the
      // opener already knew it, looked up from the run otherwise.
      let sha = params.get('sha');
      if (!sha) {
        const gh = await api.get<{ candidate: { sha: string; version: string | null } | null }>(
          `/api/releases/candidates/${runId}`,
        );
        sha = gh.candidate?.sha ?? null;
      }
      if (!sha) {
        setGate({ step: 'norelease', operator, detail: 'This run is not in the release picture yet.' });
        return;
      }
      const list = await missionOps.candidates(50);
      const hit = list.candidates.find((r) => r.gitSha.startsWith(sha) || sha.startsWith(r.gitSha));
      if (!hit) {
        setGate({
          step: 'norelease',
          operator,
          detail: `No release candidate carries ${sha.slice(0, 8)} in the registry. It may still be registering.`,
        });
        return;
      }
      setGate({ step: 'ready', operator, candidateId: hit.id });
    } catch (err) {
      setGate({
        step: 'norelease',
        operator,
        detail: err instanceof Error ? err.message : 'The release registry did not answer.',
      });
    }
  };

  useEffect(() => {
    void missionOps
      .me()
      .then((operator) => void resolveCandidate(operator))
      .catch(() => setGate({ step: 'signin' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const doLogin = async () => {
    setBusy(true);
    try {
      const r = await missionOps.login(email.trim(), password);
      if ('challengeToken' in r) {
        setGate({ step: 'mfa', challengeToken: r.challengeToken });
      } else {
        const operator = await missionOps.me();
        await resolveCandidate(operator);
      }
    } catch (err) {
      setGate({ step: 'signin', error: err instanceof MissionError ? err.message : 'Sign-in failed.' });
    } finally {
      setBusy(false);
      setPassword('');
    }
  };

  const doMfa = async (challengeToken: string) => {
    setBusy(true);
    try {
      await missionOps.mfaVerify(challengeToken, code.trim());
      const operator = await missionOps.me();
      await resolveCandidate(operator);
    } catch (err) {
      setGate({
        step: 'mfa',
        challengeToken,
        error: err instanceof MissionError ? err.message : 'The code was not accepted.',
      });
    } finally {
      setBusy(false);
      setCode('');
    }
  };

  if (rolloutId) {
    return <RolloutWatch rolloutId={rolloutId} onBack={() => navigate('/releases')} />;
  }

  return (
    <Screen
      eyebrow="Releases"
      title="The promotion ceremony"
      sub="The only path to hospitals. Same door, same gates, rendered here."
      actions={
        <Btn kind="ghost" onClick={() => navigate(`/releases/${runId}`)}>
          <ArrowLeft size={16} /> Back
        </Btn>
      }
    >
      {gate.step === 'checking' || gate.step === 'resolve' ? (
        <EmptyState icon={<Spinner />} title={gate.step === 'checking' ? 'Checking who you are…' : 'Finding the release…'} />
      ) : null}

      {gate.step === 'signin' ? (
        <div className="ops-card" style={{ maxWidth: 420 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <ShieldCheck size={20} color="var(--ops-accent)" />
            <div>
              <div className="ops-row__title">Sign in as the Console owner</div>
              <div className="ops-row__sub">The Ops passkey does not open this door.</div>
            </div>
          </div>
          <Field label="Email">
            <input
              className="ops-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <div style={{ height: 10 }} />
          <Field label="Password">
            <input
              className="ops-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && email && password && void doLogin()}
            />
          </Field>
          {gate.error ? <p className="ops-error">{gate.error}</p> : null}
          <div style={{ marginTop: 14 }}>
            <Btn kind="primary" big disabled={busy || !email.trim() || !password} onClick={() => void doLogin()}>
              {busy ? <Spinner /> : 'Continue'}
            </Btn>
          </div>
        </div>
      ) : null}

      {gate.step === 'mfa' ? (
        <div className="ops-card" style={{ maxWidth: 420 }}>
          <div className="ops-row__title" style={{ marginBottom: 10 }}>
            Authenticator code
          </div>
          <Field label="Six digits from your authenticator app">
            <input
              className="ops-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && code.trim().length >= 6 && void doMfa(gate.challengeToken)}
            />
          </Field>
          {gate.error ? <p className="ops-error">{gate.error}</p> : null}
          <div style={{ marginTop: 14 }}>
            <Btn kind="primary" big disabled={busy || code.trim().length < 6} onClick={() => void doMfa(gate.challengeToken)}>
              {busy ? <Spinner /> : 'Verify'}
            </Btn>
          </div>
        </div>
      ) : null}

      {gate.step === 'norelease' ? (
        <EmptyState title="Not promotable from here">{gate.detail}</EmptyState>
      ) : null}

      {gate.step === 'ready' ? (
        <>
          <p className="ops-hint" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Badge tone="ok">signed in</Badge> {gate.operator.displayName} · Console {gate.operator.role}
            <button
              type="button"
              className="ops-linklike"
              style={{ minHeight: 32 }}
              onClick={() => void missionOps.logout().then(() => setGate({ step: 'signin' }))}
            >
              <LogOut size={12} style={{ verticalAlign: '-1px' }} /> Sign out
            </button>
          </p>
          <PromotionCeremony
            candidateId={gate.candidateId}
            onBack={() => navigate(`/releases/${runId}`)}
            onStarted={(rid) => navigate(`/releases/${runId}/promote?rollout=${rid}`)}
            client={missionOps}
            notify={notify}
          />
        </>
      ) : null}

      {toasts.length ? (
        <div className="ops-toasts">
          {toasts.map((t) => (
            <div key={t.id} className={`ops-toast${t.tone === 'error' ? ' ops-toast--error' : ''}`}>
              <b>{t.text}</b>
              {t.detail ? <span>{t.detail}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
    </Screen>
  );
}

// Watching a rollout from the phone or the desk: waves, hospital by
// hospital, read from the same rollout view the Console renders.
function RolloutWatch(props: { rolloutId: string; onBack: () => void }) {
  const [view, setView] = useState<RolloutView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stop = false;
    const load = () =>
      missionOps
        .rollout(props.rolloutId)
        .then((v) => !stop && setView(v))
        .catch((e) => !stop && setError(e instanceof Error ? e.message : 'Unreadable'));
    void load();
    const t = setInterval(() => void load(), 5000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [props.rolloutId]);

  const tone = (s?: string) =>
    s === 'done' || s === 'completed' || s === 'complete' ? 'ok' : s === 'failed' || s === 'error' ? 'bad' : s === 'running' || s === 'migrating' || s === 'active' ? 'run' : 'wait';

  return (
    <Screen
      eyebrow="Releases"
      title={view ? `Rollout · ${view.candidate.version}` : 'Rollout'}
      sub={view ? `Started by ${view.startedByName} · ${ago(view.createdAt)} ago` : undefined}
      actions={
        <Btn kind="ghost" onClick={props.onBack}>
          <ArrowLeft size={16} /> Releases
        </Btn>
      }
    >
      {error ? <EmptyState title="Could not read the rollout.">{error}</EmptyState> : null}
      {!view && !error ? <EmptyState icon={<Spinner />} title="Reading the rollout…" /> : null}
      {view ? (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <Badge tone={tone(view.state)} pulse={!view.completedAt && !view.error}>
              {view.state}
            </Badge>
            {view.pauseReason ? <span className="ops-hint">paused: {view.pauseReason}</span> : null}
          </div>
          {view.error ? <p className="ops-error">{view.error}</p> : null}
          {view.waves.map((w) => (
            <Section key={w.id} title={w.name} aside={`${w.tenants.length} hospital${w.tenants.length === 1 ? '' : 's'}`}>
              <Rows>
                {w.tenants.map((t) => {
                  const s = t.state ?? t.status;
                  return (
                    <div key={t.tenantId} className="ops-row">
                      <div className="ops-row__main">
                        <div className="ops-row__title">{t.displayName ?? t.slug ?? t.tenantId}</div>
                        {t.error ? <div className="ops-row__sub" style={{ color: 'var(--ops-bad)' }}>{t.error}</div> : null}
                      </div>
                      <div className="ops-row__end">
                        <Badge tone={tone(s)}>{s ?? '—'}</Badge>
                      </div>
                    </div>
                  );
                })}
              </Rows>
            </Section>
          ))}
          <p className="ops-hint">
            Pause, resume and abort live in the Console's rollout board; this view watches.
          </p>
        </>
      ) : null}
    </Screen>
  );
}
